import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import Layout from './components/Layout';
import LegalWatermark from './components/LegalWatermark';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PasswordChangePage from './pages/PasswordChangePage';
import NDASignaturePage from './pages/NDASignaturePage';
import AppTutorialPage from './pages/AppTutorialPage';
import OverwatchPage from './pages/OverwatchPage';
import DashboardPage from './pages/DashboardPage';
import CustomersPage from './pages/CustomersPage';
import InvoicingPage from './pages/InvoicingPage';
import ReportsPage from './pages/ReportsPage';
import CompliancePage from './pages/CompliancePage';
import ProfilePage from './pages/ProfilePage';
// ============================================================
// Innovation Hub Feature Pages
// ============================================================
import AIIntakePage from './pages/AIIntakePage';
import IdeaMarketplacePage from './pages/IdeaMarketplacePage';
import AppGeneratorPage from './pages/AppGeneratorPage';
import WebsiteBuilderPage from './pages/WebsiteBuilderPage';
import LegalDocGeneratorPage from './pages/LegalDocGeneratorPage';
import PatentGeneratorPage from './pages/PatentGeneratorPage';
import PortfolioPage from './pages/PortfolioPage';
import CoDevPage from './pages/CoDevPage';
import LicenseActivationPage from './pages/LicenseActivationPage';
import LogoGeneratorPage from './pages/LogoGeneratorPage';

// ============================================================
// ADMIN TEAM — Users who get owner-level access
// Add team member emails here (personal + company)
// ============================================================
const ADMIN_TEAM = [
  'J.lutz@miskulintechnologies.com',
  'R.miskulin@miskulintechnologies.com',
  'rmiskulin@gmail.com',
  'criodtech@gmail.com',
  'threewayindustries@gmail.com',
  'desbachynski@gmail.com',
  'lucas.s.mason@gmail.com',
  'leemehling@hotmail.com',
  'cloricer77@gmail.com',
  'tyson@canstorr.com',
  'tyler.hodgson32@gmail.com',
  'jhansen860@yahoo.com',
  'littlesderek@gmail.com',
  'laken.weston@live.ca',
  'che@rosebudcannabisfarms.ca',
  'shanon@lanewaymedia.ca',
  'amandakristaleigh@hotmail.com',
  'dblahut@sastel.net',
  'demo@iqapps.dev',
  // Mt Forge team
  'j.lutz@mtforge.ai',
  'r.miskulin@mtforge.ai',
  't.schenher@mtforge.ai',
  'c.leblanc@mtforge.ai',
  'd.bachynski@mtforge.ai',
];

export { ADMIN_TEAM };

function RoleHomePage() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (ADMIN_TEAM.includes(user.email)) return <Navigate to="/overwatch" />;
  if (user.role === 'owner') return <Navigate to="/overwatch" />;
  return <Navigate to="/dashboard" />;
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--bg)', color: 'var(--text)', fontSize: 18 }}>Loading...</div>;
  return user ? children : <Navigate to="/login" />;
}

function OnboardingGate({ children }) {
  const { user, onboardingStep, completeOnboardingStep } = useAuth();
  if (!user) return children;
  if (onboardingStep === 'password') return <PasswordChangePage onComplete={() => completeOnboardingStep('password')} />;
  if (onboardingStep === 'nda') return <NDASignaturePage onComplete={() => completeOnboardingStep('nda')} />;
  if (onboardingStep === 'tutorial') return <AppTutorialPage onComplete={() => completeOnboardingStep('tutorial')} />;
  return children;
}

export default function App() {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--bg)', color: 'var(--text)', fontSize: 18 }}>Loading...</div>;

  return (
    <>
    <LegalWatermark appName="The Forge Standard" />
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/overwatch" element={<PrivateRoute><OverwatchPage /></PrivateRoute>} />
      <Route path="/" element={<PrivateRoute><OnboardingGate><Layout /></OnboardingGate></PrivateRoute>}>
        <Route index element={<RoleHomePage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="invoicing" element={<InvoicingPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="compliance" element={<CompliancePage />} />
        <Route path="profile" element={<ProfilePage />} />
        {/* Innovation Engine */}
        <Route path="ai-intake" element={<AIIntakePage />} />
        <Route path="ideas" element={<IdeaMarketplacePage />} />
        <Route path="app-generator" element={<AppGeneratorPage />} />
        <Route path="website-builder" element={<WebsiteBuilderPage />} />
        <Route path="logo-generator" element={<LogoGeneratorPage />} />
        {/* Legal & IP */}
        <Route path="legal-generator" element={<LegalDocGeneratorPage />} />
        <Route path="patent-generator" element={<PatentGeneratorPage />} />
        {/* Business */}
        <Route path="portfolio" element={<PortfolioPage />} />
        <Route path="co-dev" element={<CoDevPage />} />
        {/* System */}
        <Route path="license-activation" element={<LicenseActivationPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </>
  );
}
